import React from 'react'
import { Container, SortableFinder } from '../'
import { Folder, Unit, UnitVisibility } from '../../models'
import { Checkbox, Tooltip, useTheme } from '@material-ui/core'
import { LocationSelection, Location } from '../../store'

interface Props {
    root: Folder
    locationSelection: LocationSelection
    selectedIcon?: JSX.Element
    setLocationSelection: (location: Location) => void
    getUnitAdornment?: (unit: Unit) => JSX.Element | null
    getFolderAdornment?: (folder: Folder) => JSX.Element | null
    style?: React.CSSProperties
    defaultOpen?: boolean
    defaultOpenFirstLevel?: boolean
    useCheckbox?: boolean
    useCase?: 'inspection' | 'schedule'
    enableSorting?: boolean
    defaultSortOrder?: 'asc' | 'desc'
    onSortChange?: (folderId: number, order: 'asc' | 'desc') => void
    onUnitOrderChange?: (folderId: number, unitIds: number[]) => void
    maxHeight?: string | number
    disabledUnitIds?: Set<number>
    disabledUnitReasonById?: Record<number, string>
}

export const SortableLocationForm = (props: Props) => {
    const theme = useTheme()
    const {
        setLocationSelection,
        locationSelection,
        useCase,
        enableSorting = false,
        disabledUnitIds,
        disabledUnitReasonById,
    } = props

    let { selectedIcon, getUnitAdornment, getFolderAdornment } = props

    if (props.useCheckbox) {
        selectedIcon = <></>
        getUnitAdornment = (unit) => {
            const unitIsSelectionLocked =
                disabledUnitIds?.has(unit.id) === true &&
                locationSelection.unit[unit.id] === undefined
            const tooltipText =
                disabledUnitReasonById?.[unit.id] ?? 'another phase'
            return (
                <Container style={{ flex: 1 }}>
                    <div style={{ flex: 1 }} />
                    {unitIsSelectionLocked ? (
                        <Tooltip title={`Selected in ${tooltipText}`}>
                            <span>
                                <Checkbox
                                    checked={
                                        locationSelection.unit[unit.id] !==
                                        undefined
                                    }
                                    disabled
                                    onClick={() => undefined}
                                />
                            </span>
                        </Tooltip>
                    ) : (
                        <Checkbox
                            checked={
                                locationSelection.unit[unit.id] !== undefined
                            }
                            onClick={() => {
                                setLocationSelection({
                                    type: 'unit',
                                    location: unit,
                                })
                            }}
                        />
                    )}
                </Container>
            )
        }

        getFolderAdornment = (folder) => {
            // Get all units in this folder and its children recursively
            const getAllUnits = (folder: Folder): Unit[] => {
                let units = [...folder.units]
                folder.children.forEach((child) => {
                    units = units.concat(getAllUnits(child))
                })
                return units
            }

            const allUnits = getAllUnits(folder)
            const selectableUnits = allUnits.filter((unit) => {
                const unitIsSelectionLocked =
                    disabledUnitIds?.has(unit.id) === true &&
                    locationSelection.unit[unit.id] === undefined
                return !unitIsSelectionLocked
            })
            const selectableUnitIds = selectableUnits.map((unit) => unit.id)

            // Check folder state
            const allSelected =
                selectableUnitIds.length > 0 &&
                selectableUnitIds.every(
                    (id) => locationSelection.unit[id] !== undefined,
                )
            const someSelected = selectableUnitIds.some(
                (id) => locationSelection.unit[id] !== undefined,
            )
            const folderSelectionDisabled = selectableUnitIds.length === 0

            return (
                <Container style={{ flex: 1 }}>
                    <div style={{ flex: 1 }} />
                    <Checkbox
                        color={'primary'}
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        disabled={folderSelectionDisabled}
                        onClick={(e) => {
                            e.stopPropagation()
                            if (folderSelectionDisabled) return
                            setLocationSelection({
                                type: 'folder',
                                location: folder,
                            })
                        }}
                    />
                </Container>
            )
        }
    }

    return (
        <Container
            flex={1}
            direction="column"
            style={{
                padding: theme.spacing(2),
                overflow: 'hidden',
                ...props.style,
            }}
        >
            <SortableFinder
                {...props}
                onClickUnit={(unit) => {
                    if (
                        unit.type === 'unit' &&
                        disabledUnitIds?.has(unit.location.id) === true &&
                        locationSelection.unit[unit.location.id] === undefined
                    ) {
                        return
                    }

                    setLocationSelection(unit)
                }}
                onClickFolder={(folder) => setLocationSelection(folder)}
                selectedIcon={selectedIcon}
                getUnitAdornment={getUnitAdornment}
                getFolderAdornment={getFolderAdornment}
                enableSorting={enableSorting}
                onUnitOrderChange={props.onUnitOrderChange}
                maxHeight={props.maxHeight}
            />
        </Container>
    )
}
